import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Plane, Ship, Truck, Globe, FileCheck, Handshake,
  Shield, Zap, CheckCircle, MapPin, Phone, Clock, ChevronRight,
  Package, Search, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  companyInfo, services, coreValues, whyChooseUs, stats, 
  testimonials, turnaroundTimes, mockShipments
} from '../data/mock';

const iconMap = {
  Plane: Plane,
  Ship: Ship,
  Truck: Truck,
  Globe: Globe,
  FileCheck: FileCheck,
  Handshake: Handshake,
  Shield: Shield,
  Zap: Zap,
  CheckCircle: CheckCircle
};

const HomePage = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1605745341112-85968b19335b?w=1920&q=80"
            alt="Cargo Ship"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B4F5D]/95 via-[#1B4F5D]/80 to-transparent" />
        </div>

        <div className="container mx-auto px-6 py-32 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm mb-6 animate-fadeIn">
              <Ship size={16} className="text-[#408D4D]" />
              Ghana's Trusted Logistics Partner
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fadeInUp">
              Seamless Logistics Solutions Across
              <span className="text-[#408D4D]"> Borders</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              Cross-border cargo handling, customs clearance, and export logistics. 
              We ensure smooth and compliant movement of goods within and beyond Ghana.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
              <Link to="/contact">
                <Button className="bg-[#408D4D] hover:bg-[#2D6636] text-white px-8 py-6 text-lg rounded-lg font-semibold transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#408D4D]/30">
                  Get a Free Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1B4F5D] px-8 py-6 text-lg rounded-lg font-semibold transition-all duration-300">
                  Our Services
                </Button>
              </Link>
            </div>

            {/* Quick Track */}
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl max-w-xl animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
              <p className="text-white text-sm mb-3 font-medium">Track Your Shipment</p>
              <div className="flex gap-3">
                <Input
                  type="text"
                  placeholder="Enter tracking number..."
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="bg-white/90 border-0 text-[#1B4F5D] placeholder:text-gray-500 h-12 text-base"
                />
                <Link to={`/tracking${trackingNumber ? `?id=${trackingNumber}` : ''}`}>
                  <Button className="bg-[#408D4D] hover:bg-[#2D6636] text-white px-6 h-12">
                    <Search size={20} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/50 flex items-start justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#1B4F5D] py-8">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-[#408D4D] mb-2">
                  {stat.value}
                </div>
                <div className="text-white/80 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" data-animate className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">What We Offer</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B4F5D] mt-3 mb-4">
              Our Services
            </h2>
            <p className="text-gray-600 text-lg">
              Comprehensive logistics solutions tailored to meet your cross-border trade needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon] || Package;
              return (
                <Card 
                  key={service.id} 
                  className={`group bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden card-hover ${
                    isVisible.services ? 'animate-fadeInUp' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-[#408D4D]/10 flex items-center justify-center mb-6 group-hover:bg-[#408D4D] transition-colors duration-300">
                      <Icon className="text-[#408D4D] group-hover:text-white transition-colors duration-300" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-[#1B4F5D] mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-500">
                          <CheckCircle size={14} className="text-[#408D4D]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="border-2 border-[#1B4F5D] text-[#1B4F5D] hover:bg-[#1B4F5D] hover:text-white px-8 py-3 rounded-lg font-semibold">
                View All Services
                <ChevronRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">Why Choose Us</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1B4F5D] mt-3 mb-6">
                Your Trusted Partner in Ghana Logistics
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                With deep knowledge of local trade regulations and port operations, 
                we deliver excellence in every shipment.
              </p>

              <div className="space-y-6">
                {whyChooseUs.map((item, index) => (
                  <div key={item.id} className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-[#408D4D] flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={18} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1B4F5D] mb-1">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1611224111800-0eaf3e53aa45?w=800&q=80"
                alt="Container Operations"
                className="rounded-2xl shadow-2xl"
              />
              
              {/* Turnaround Times Card */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <h4 className="font-bold text-[#1B4F5D] mb-4 flex items-center gap-2">
                  <Clock size={20} className="text-[#408D4D]" />
                  Turnaround Times
                </h4>
                <ul className="space-y-2">
                  {turnaroundTimes.slice(0, 3).map((item, index) => (
                    <li key={index} className="flex justify-between text-sm">
                      <span className="text-gray-600">{item.service}</span>
                      <span className="font-semibold text-[#408D4D]">{item.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 bg-[#1B4F5D]">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">Our Foundation</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              Core Values
            </h2>
            <p className="text-gray-300 text-lg">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {coreValues.map((value, index) => {
              const Icon = iconMap[value.icon] || Shield;
              return (
                <div 
                  key={value.id}
                  className="text-center p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#408D4D] flex items-center justify-center mb-6">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-300">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B4F5D] mt-3 mb-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="absolute top-8 left-8 text-[#408D4D]/20">
                <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>
              
              <div className="relative z-10">
                <p className="text-xl md:text-2xl text-gray-700 italic mb-8 leading-relaxed">
                  "{testimonials[activeTestimonial].text}"
                </p>
                
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#1B4F5D] flex items-center justify-center text-white font-bold text-xl">
                    {testimonials[activeTestimonial].name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1B4F5D]">{testimonials[activeTestimonial].name}</h4>
                    <p className="text-gray-600 text-sm">{testimonials[activeTestimonial].company}</p>
                  </div>
                  <div className="ml-auto flex gap-1">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                      <Star key={i} size={18} className="fill-[#408D4D] text-[#408D4D]" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Navigation dots */}
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === activeTestimonial 
                        ? 'bg-[#408D4D] w-8' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/2881632/pexels-photo-2881632.jpeg?auto=compress&w=1920"
            alt="Port Operations"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1B4F5D]/90" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Ship with Confidence?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Let us handle your logistics while you focus on growing your business. 
              Get a free quote today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-[#408D4D] hover:bg-[#2D6636] text-white px-10 py-6 text-lg rounded-lg font-semibold transition-all duration-300 hover:-translate-y-1">
                  Get Started Today
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/tracking">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1B4F5D] px-10 py-6 text-lg rounded-lg font-semibold">
                  Track Shipment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
